import {prepareAgencyProps} from './agency';
import {isArray} from './boolean';
import {dateFormat} from './date';
import {toNumber} from './number';
import {getObjectProp} from './object';
import {DATE_FORMAT_DISPLAY} from '../constants/date';
import * as consts from '../constants/property';

/**
 * Gets the list of property images
 *
 * @param {object} property
 *
 * @return {array}
 */
export const propertyImages = property => {
  if (getObjectProp(property, consts.API_PROPERTY_IMAGES)) {
    const images = property[consts.API_PROPERTY_IMAGES];
    if (isArray(images)) {
      return images;
    }
  }
  return [];
};

/**
 *
 * @param {array} images
 * @returns {array}
 */
export const preparePropertyImages = images => {
  if (!isArray(images)) {
    return [];
  }
  return images.map(image => ({
    id: getObjectProp(image, 'id'),
    name: getObjectProp(image, 'nice_name'),
    src: getObjectProp(image, 'large_url'),
    thumbSrc: getObjectProp(image, 'thumbnail_url'),
  }));
};

/**
 * Gets the first image of property from list
 *
 * @param {object|array} data - object of property or array of images
 *
 * @return {string}
 */
export const propertyMainImage = data => {
  const images = isArray(data) ? data : propertyImages(data);
  if (images.length > 0) {
    return (
      getObjectProp(images[0], 'large_url') || getObjectProp(images[0], 'url')
    );
  }
  return '';
};

export const parsePropertyAddress = property => {
  const clean = part => String(part).trim();

  const number = [
    getObjectProp(property, consts.API_PROPERTY_ADDR_UNIT),
    getObjectProp(property, consts.API_PROPERTY_ADDR_STREET_NUMBER),
  ]
    .map(clean)
    .filter(Boolean);

  const part1 = [
    number,
    getObjectProp(property, consts.API_PROPERTY_ADDR_STREET),
  ]
    .map(clean)
    .filter(Boolean)
    .join(' ');

  const part2 = [
    getObjectProp(property, consts.API_PROPERTY_ADDR_SUBURB),
    getObjectProp(property, consts.API_PROPERTY_ADDR_CITY),
  ]
    .map(clean)
    .filter(Boolean)
    .join(', ');

  return {
    addressPart1: part1,
    addressPart2: part2,
    fullAddress: `${part1}, ${part2}`,
  };
};

export const parsePropertyRent = property => {
  const rentAmount = toNumber(getObjectProp(property, 'rent_amount', 0), 0);
  const rentFrequency = getObjectProp(property, 'rental_period').trim();

  return ['$', rentAmount, ` ${rentFrequency}`]
    .filter((v, _, items) => {
      if (!items[1]) {
        return false;
      }
      return Boolean(v);
    })
    .join('');
};

export const propertyFullAddress = property => {
  const {fullAddress} = parsePropertyAddress(property);
  return fullAddress;
};

export const preparePropertyExtraProps = (property = {}) => ({
  description: getObjectProp(property, 'property_feature.description', null),
  latitude: getObjectProp(property, 'property_feature.latitude', null),
  longitude: getObjectProp(property, 'property_feature.longitude', null),
});

/**
 * Transforms api results to object which is parsed to React components
 *
 * @param {object} property
 *
 * @return {object}
 */
export const preparePropertyProps = property => {
  const images = propertyImages(property);
  const {addressPart1, addressPart2, fullAddress} =
    parsePropertyAddress(property);

  const availableAt = getObjectProp(property, 'available_at', null);

  return {
    agentName: getObjectProp(property, consts.API_PROPERTY_AGENT_NAME),
    city: getObjectProp(property, consts.API_PROPERTY_ADDR_CITY),
    availableAt,
    availableAtDisplay: availableAt
      ? dateFormat(availableAt, DATE_FORMAT_DISPLAY)
      : availableAt,
    email: getObjectProp(property, consts.API_PROPERTY_EMAIL),
    mobile: getObjectProp(property, consts.API_PROPERTY_MOBILE),
    id: getObjectProp(property, consts.API_PROPERTY_ADDR_ID),
    images,
    postcode: getObjectProp(property, consts.API_PROPERTY_ADDR_POSTCODE),
    primaryImage:
      getObjectProp(images[0], 'url') ||
      getObjectProp(images[0], 'medium_url') ||
      getObjectProp(property, 'thumb_url', null),
    street: getObjectProp(property, consts.API_PROPERTY_ADDR_STREET),
    streetNumber: getObjectProp(
      property,
      consts.API_PROPERTY_ADDR_STREET_NUMBER
    ),
    suburb: getObjectProp(property, consts.API_PROPERTY_ADDR_SUBURB),
    unit: getObjectProp(property, consts.API_PROPERTY_ADDR_UNIT),
    fullAddress,
    shortAddress: addressPart1,
    addressPart1,
    addressPart2,
    noOfBedrooms: getObjectProp(property, 'num_bedrooms', null),
    noOfBathrooms: getObjectProp(property, 'num_bathrooms', null),
    noOfParking: getObjectProp(property, 'num_cars', null),
    rent: parsePropertyRent(property),
  };
};

export const preparePropertyDetailProps = (property = {}) => ({
  ...preparePropertyProps(property),
  ...preparePropertyExtraProps(property),
  images: preparePropertyImages(property.images),
  agency: prepareAgencyProps(property.agency || {}),
});

export const preparePropertyRemarketingListProps = items => {
  if (!isArray(items)) {
    return [];
  }
  return items.map(item => ({
    agency: prepareAgencyProps(item.agency || {}),
    ...preparePropertyProps(item),
  }));
};
